/**
 * Remember where a visitor came from, so the signup can say which campaign
 * produced it.
 *
 * Captured on the first page the visitor lands on and kept for the session:
 * by the time they reach the signup form the URL no longer carries the UTM
 * parameters, and the referrer is long gone. Nothing here identifies a
 * person — it is campaign metadata, not visitor tracking.
 */

const STORAGE_KEY = "humo_acquisition";

export interface AcquisitionPayload {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  /** When the fbclid was seen (ms epoch) — Meta's _fbc carries the click time. */
  fbclid_at?: number;
  referrer?: string;
  landing_path?: string;
}

const PARAMS: Array<Exclude<keyof AcquisitionPayload, "fbclid_at">> = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid"
];

/** Call once on app start. First touch wins: a later internal page must not overwrite it. */
export const captureAcquisition = (): void => {
  try {
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    const query = new URLSearchParams(window.location.search);
    const payload: AcquisitionPayload = {};
    for (const key of PARAMS) {
      const value = query.get(key);
      if (value) payload[key] = value.slice(0, 200);
    }

    if (payload.fbclid) payload.fbclid_at = Date.now();

    // An external referrer is worth keeping even without UTM parameters;
    // our own domain is not.
    const referrer = document.referrer;
    if (referrer && !referrer.includes(window.location.host)) {
      payload.referrer = referrer.slice(0, 500);
    }
    payload.landing_path = `${window.location.pathname}${window.location.search}`.slice(0, 500);

    const hasSource = Object.keys(payload).some((key) => key !== "landing_path");
    if (!hasSource && !payload.landing_path) return;

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Private mode / storage disabled — attribution is best-effort.
  }
};

/** What to send with the signup request. */
export const readAcquisition = (): AcquisitionPayload | undefined => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as AcquisitionPayload) : undefined;
  } catch {
    return undefined;
  }
};
